import { isUnknownUpdate, parseUpdate } from "@sooth-labs/idl";
import { isTerminal } from "./model.js";
import type { RunAction, RunState } from "./reducer.js";
import { runUpdateApplied } from "./reducer.js";

const hasInvalidOptionalText = (value: unknown): boolean =>
  value !== undefined && typeof value !== "string";

const RUN_OUTCOMES = new Set(["completed", "inconclusive", "failed", "cancelled"]);

const hasInvalidRenderableBody = (update: ReturnType<typeof parseUpdate>): boolean => {
  if (isUnknownUpdate(update)) return false;
  if (update.kind === "run_finished") return !RUN_OUTCOMES.has(update.outcome);
  if (update.kind === "past_trigger_upserted" || update.kind === "future_trigger_upserted") {
    return (
      update.trigger === null ||
      typeof update.trigger !== "object" ||
      typeof update.trigger.id !== "string" ||
      update.trigger.id.trim().length === 0 ||
      typeof update.trigger.title !== "string"
    );
  }
  if (
    update.kind === "tool_call_started" ||
    update.kind === "tool_call_updated" ||
    update.kind === "tool_call_finished"
  ) {
    return hasInvalidOptionalText(update.title);
  }
  if (update.kind === "report_updated") {
    if (update.report === null || typeof update.report !== "object") return true;
    return hasInvalidOptionalText((update.report as unknown as Record<string, unknown>).summary);
  }
  return false;
};

/** Parse and dispatch one typed update while its target run remains live. */
export function applyUpdateFrame(
  dispatch: (action: RunAction) => void,
  getState: () => Pick<RunState, "runsById" | "sharedRunsById">,
  runId: string,
  frame: unknown,
): boolean {
  const state = getState();
  const run = state.runsById[runId] ?? state.sharedRunsById?.[runId];
  if (!run || isTerminal(run.status) || run.progressDoc?.outcome != null) return false;
  const payload =
    frame !== null && typeof frame === "object" ? (frame as Record<string, unknown>) : {};
  const update = parseUpdate(payload);
  if (hasInvalidRenderableBody(update)) return false;
  dispatch(runUpdateApplied(runId, update));
  return true;
}
